import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Clock } from "lucide-react";

const buckets = ["0-30", "31-60", "61-90", "90+"];
const heat = {
  "0-30": { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800" },
  "31-60": { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800" },
  "61-90": { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-800" },
  "90+": { bg: "bg-red-50", border: "border-red-200", text: "text-red-800" },
};

export default function Ageing() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => {
    const pf = selectedProject && selectedProject !== "__ALL__" ? selectedProject : "";
    const q = pf ? `?project_id=${pf}` : "";
    api.get(`/reports/ageing${q}`).then(r => setData(r.data));
  }, [selectedProject]);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Reports · Ageing</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Ageing Heat-Map</h1>
        <p className="text-sm text-slate-500 mt-1">Overdue advances and vendor payables bucketed by age. Nothing should slip past 60 days.</p>
      </div>

      <Card className="mb-6 max-w-sm">
        <CardContent className="p-4">
          <Label>Project (or all)</Label>
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger data-testid="ageing-project-select"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">All projects</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {!data ? <div className="text-slate-500 text-sm">Loading…</div> : (
        <Tabs defaultValue="advances">
          <TabsList className="mb-4">
            <TabsTrigger value="advances" data-testid="ageing-tab-advances">Advances (₹ {data.grand_total.advances.toLocaleString()})</TabsTrigger>
            <TabsTrigger value="payables" data-testid="ageing-tab-payables">Vendor Payables (₹ {data.grand_total.vendor_payables.toLocaleString()})</TabsTrigger>
          </TabsList>
          <TabsContent value="advances"><AgeingSection section={data.advances} /></TabsContent>
          <TabsContent value="payables"><AgeingSection section={data.vendor_payables} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function AgeingSection({ section }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {buckets.map(b => {
          const t = section.totals[b];
          const style = heat[b];
          return (
            <Card key={b} className={`${style.bg} ${style.border}`} data-testid={`bucket-${b}`}>
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider font-semibold flex items-center gap-1">
                  {b === "90+" && <AlertTriangle className="w-3 h-3" />}
                  <span className={style.text}>{b} days</span>
                </div>
                <div className="mt-2 font-display font-bold text-2xl">₹ {t.amount.toLocaleString()}</div>
                <div className={`text-xs mt-1 ${style.text}`}>{t.count} item{t.count !== 1 ? "s" : ""}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="space-y-3">
        {buckets.map(b => {
          const items = section.buckets[b];
          if (!items.length) return null;
          const style = heat[b];
          return (
            <Card key={b} className={style.border}>
              <CardContent className="p-0">
                <div className={`px-4 py-2 ${style.bg} border-b ${style.border} text-xs font-semibold uppercase tracking-wider ${style.text}`}>{b} days · {items.length} item{items.length !== 1 ? "s" : ""}</div>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 bg-slate-50"><tr className="border-b"><th className="text-left px-3 py-2">Reference</th><th className="text-right px-3 py-2">Amount</th><th className="text-right px-3 py-2">Age</th></tr></thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it.id} className="border-b border-slate-100">
                        <td className="px-3 py-2">{it.label}</td>
                        <td className="px-3 py-2 text-right font-mono">₹ {(it.amount||0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right"><Clock className="inline w-3 h-3 mr-1 text-slate-400" /><span className={`font-mono ${style.text}`}>{it.days}d</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
